import React, { useState } from "react";
import { ArrowDown, ArrowUp, EyeOff, Eye, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { useOps } from "@/lib/opsCards";
import { inputCls } from "./FormRenderer";

const TYPE_LABELS = { text: "Text", number: "Number", money: "Money", date: "Date", select: "Dropdown", boolean: "Yes / No", textarea: "Notes", email: "Email", phone: "Phone", project: "Project picker" };
const blank = () => ({ label: "", type: "text", required: false, options: [], default: "", help: "" });

function FieldRow({ f, i, n, builtin, update, move, remove }) {
  const locked = builtin;
  return (
    <div data-testid={`form-builder-field-${i}`} className="rounded-lg border border-slate-800 bg-navy-950/60 p-3 space-y-2">
      <div className="flex gap-2 items-center">
        <input value={f.label} onChange={(e) => update({ label: e.target.value })} placeholder="Field label" data-testid={`form-builder-label-${i}`} className={`${inputCls} flex-1`} />
        <select value={f.type} disabled={locked} onChange={(e) => update({ type: e.target.value })} data-testid={`form-builder-type-${i}`} className={`${inputCls} w-36`}>
          {Object.entries(TYPE_LABELS).filter(([t]) => locked || t !== "project").map(([t, l]) => <option key={t} value={t}>{l}</option>)}
        </select>
        <button type="button" onClick={() => move(-1)} disabled={i === 0} aria-label="Move up" data-testid={`form-builder-up-${i}`} className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => move(1)} disabled={i === n - 1} aria-label="Move down" data-testid={`form-builder-down-${i}`} className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
        {builtin ? (
          <button type="button" onClick={() => update({ hidden: !f.hidden })} disabled={f.required} aria-label="Toggle hidden" data-testid={`form-builder-hide-${i}`}
            title={f.required ? "Required fields can't be hidden" : f.hidden ? "Show" : "Hide"} className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30">
            {f.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <button type="button" onClick={remove} aria-label="Remove field" data-testid={`form-builder-remove-${i}`} className="p-1.5 text-slate-500 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
      {f.type === "select" && !locked && (
        <input value={(f.options || []).join(", ")} onChange={(e) => update({ options: e.target.value.split(",").map((s) => s.trimStart()) })}
          placeholder="Options, separated by commas" data-testid={`form-builder-options-${i}`} className={inputCls} />
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <input value={f.default ?? ""} onChange={(e) => update({ default: e.target.value })} placeholder="Default (optional)" data-testid={`form-builder-default-${i}`} className={`${inputCls} flex-1 min-w-[120px]`} />
        <input value={f.help || ""} onChange={(e) => update({ help: e.target.value })} placeholder="Help text (optional)" data-testid={`form-builder-help-${i}`} className={`${inputCls} flex-[2] min-w-[160px]`} />
        {!locked && (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-300">
            <input type="checkbox" checked={f.required} onChange={(e) => update({ required: e.target.checked })} data-testid={`form-builder-required-${i}`} className="accent-cyan-400" /> Required
          </label>
        )}
      </div>
    </div>
  );
}

// Build a custom form, or rename / reorder / hide fields on a built-in one.
export default function FormBuilder({ form, onSaved, onCancel }) {
  const { reload } = useOps();
  const builtin = form?.kind === "builtin";
  const [name, setName] = useState(form?.name || "");
  const [description, setDescription] = useState(form?.description || "");
  const [fields, setFields] = useState(() => (form?.fields || []).filter((f) => !f.archived).map((f) => ({ ...f })) || []);
  const [busy, setBusy] = useState(false);
  const update = (i, patch) => setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i, d) => setFields((fs) => { const c = [...fs]; [c[i], c[i + d]] = [c[i + d], c[i]]; return c; });

  const save = async () => {
    if (!name.trim()) return toast.error("Give the form a name");
    if (!fields.length) return toast.error("Add at least one field");
    if (fields.some((f) => !f.label.trim())) return toast.error("Every field needs a label");
    setBusy(true);
    try {
      const body = { name, description, fields: fields.map((f) => ({ ...f, default: f.default === "" ? null : f.default })) };
      const { data } = form?.id ? await apiClient.put(`/admin/ops/forms/${form.id}`, body) : await apiClient.post("/admin/ops/forms", body);
      toast.success("Form saved"); await reload(); onSaved?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="form-builder" className="space-y-3">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Form name (e.g. Site Visit Log)" data-testid="form-builder-name" className={inputCls} />
      {!builtin && <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this form for? (optional)" data-testid="form-builder-description" className={inputCls} />}
      {fields.map((f, i) => (
        <FieldRow key={f.key || `new-${i}`} f={f} i={i} n={fields.length} builtin={builtin}
          update={(p) => update(i, p)} move={(d) => move(i, d)} remove={() => setFields((fs) => fs.filter((_, j) => j !== i))} />
      ))}
      {!builtin && (
        <button type="button" onClick={() => setFields((fs) => [...fs, blank()])} data-testid="form-builder-add-field"
          className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-md border border-dashed border-slate-700 text-slate-400 hover:text-brand-cyan hover:border-brand-cyan/60 font-mono text-[0.6rem] tracking-[0.14em] uppercase transition">
          <Plus className="w-3 h-3" /> Add field
        </button>
      )}
      <div className="flex justify-end gap-2 pt-1">
        {onCancel && <button type="button" onClick={onCancel} data-testid="form-builder-cancel" className="px-4 py-2 rounded-md border border-slate-700 text-slate-300 font-mono text-[0.6rem] tracking-[0.14em] uppercase hover:text-white">Cancel</button>}
        <button type="button" onClick={save} disabled={busy} data-testid="form-builder-save"
          className="px-4 py-2 rounded-md bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 font-mono text-[0.6rem] font-bold tracking-[0.14em] uppercase hover:brightness-110 disabled:opacity-60">
          {busy ? "Saving…" : "Save form"}
        </button>
      </div>
    </div>
  );
}
